import React, { useState, useEffect, useRef, useCallback } from 'react';
import { parseAamva } from './AamvaParser';

/**
 * Camera-based PDF417 scanner for US/Canadian driver's licenses.
 *
 * Strategy (in order of preference):
 *  1. BarcodeDetector Web API  — native, uses Google ML Kit on Android Chrome.
 *     Fastest, most reliable for PDF417 on Android tablets.
 *  2. @zxing/browser BrowserPDF417Reader — JavaScript fallback for browsers
 *     that don't support BarcodeDetector (iOS Safari, Firefox, older Chrome).
 *
 * Calls onScan(parsedAamvaData) when a license barcode is successfully read.
 */
export default function LicenseScanner({ onScan, onCancel }) {
  const videoRef = useRef(null);
  const stopRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | scanning | error
  const [errorMsg, setErrorMsg] = useState('');
  const [scanCount, setScanCount] = useState(0); // visible pulse so staff know it's trying

  const stopScanner = useCallback(() => {
    if (stopRef.current) {
      try { stopRef.current(); } catch (_) {}
      stopRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        // ── Path 1: native BarcodeDetector (Android Chrome 83+, Edge 83+) ──
        if ('BarcodeDetector' in window) {
          const supported = await window.BarcodeDetector.getSupportedFormats().catch(() => []);
          if (supported.includes('pdf417')) {
            await startNative(cancelled);
            return;
          }
        }
        // ── Path 2: ZXing JavaScript fallback ────────────────────────────
        await startZXing(cancelled);
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(friendlyError(e));
        }
      }
    }

    // ── Native BarcodeDetector path ─────────────────────────────────────
    async function startNative() {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 1280 },
          height: { ideal: 720  }
        }
      });

      if (cancelled) { stopStream(stream); return; }

      const video = videoRef.current;
      video.srcObject = stream;
      // autoplay attribute not reliable on Android — call play() explicitly
      await video.play().catch(() => {});

      if (cancelled) { stopStream(stream); return; }

      setStatus('scanning');

      const detector = new window.BarcodeDetector({ formats: ['pdf417'] });
      let detecting = false;
      let timer;

      const tick = async () => {
        if (cancelled) return;

        if (!detecting && video.readyState >= 2) {
          detecting = true;
          try {
            const codes = await detector.detect(video);
            if (codes.length > 0 && !cancelled) {
              const raw = codes[0].rawValue;
              console.log('[LicenseScanner] native hit, raw (first 300):', raw.substring(0, 300));
              const parsed = parseAamva(raw);
              console.log('[LicenseScanner] parsed:', parsed);
              if (parsed) {
                stopScanner();
                onScan(parsed);
                return;
              }
            }
          } catch (_) { /* individual frame errors are normal */ }
          detecting = false;
          setScanCount((n) => n + 1);
        }

        if (!cancelled) timer = setTimeout(tick, 120);
      };

      timer = setTimeout(tick, 300); // brief pause for camera to settle

      stopRef.current = () => {
        cancelled = true;
        clearTimeout(timer);
        stopStream(stream);
        if (video) { video.srcObject = null; }
      };
    }

    // ── ZXing fallback path ─────────────────────────────────────────────
    async function startZXing() {
      const { BrowserPDF417Reader } = await import('@zxing/browser');
      if (cancelled) return;

      const reader = new BrowserPDF417Reader();

      setStatus('scanning');

      // Pass undefined for deviceId → library uses { facingMode: 'environment' }
      // Do NOT enumerate devices first — on Android, device IDs obtained before
      // the permission grant are ephemeral and the { exact: id } constraint fails.
      const controls = await reader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result, err) => {
          if (cancelled) return;
          setScanCount((n) => n + 1);

          if (result) {
            const raw = result.getText();
            console.log('[LicenseScanner] ZXing hit, raw (first 300):', raw.substring(0, 300));
            const parsed = parseAamva(raw);
            console.log('[LicenseScanner] parsed:', parsed);
            if (parsed) {
              stopScanner();
              onScan(parsed);
            }
          }
          if (err && err.name !== 'NotFoundException') {
            console.warn('[LicenseScanner] ZXing error:', err.name, err.message);
          }
        }
      );

      if (cancelled) {
        controls?.stop();
      } else {
        stopRef.current = () => controls?.stop();
      }
    }

    start();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [onScan, stopScanner]);

  const handleCancel = () => {
    stopScanner();
    onCancel();
  };

  return (
    <div className="license-scanner">
      <div className="scanner-header">
        <h3>Scan License Barcode</h3>
        <p className="scanner-instructions">
          Hold the <strong>back of the license</strong> flat and steady — aim the camera at the{' '}
          <strong>PDF417 barcode</strong> (the wide rectangular striped barcode near the bottom).
        </p>
      </div>

      {/* Video always rendered so the browser can attach the stream immediately */}
      <div className="scanner-viewport">
        <video ref={videoRef} className="scanner-video" playsInline muted autoPlay />

        {status === 'starting' && (
          <div className="scanner-overlay-msg">Starting camera…</div>
        )}
        {status === 'error' && (
          <div className="scanner-overlay-msg scanner-overlay-error">{errorMsg}</div>
        )}
        {status === 'scanning' && (
          <div className="scanner-overlay">
            <div className="scanner-target" />
          </div>
        )}
      </div>

      {status === 'scanning' && (
        <p className="scanner-pulse">
          Scanning… <span className="scan-tick">{scanCount > 0 ? `(frame ${scanCount})` : ''}</span>
        </p>
      )}

      <div className="scanner-actions">
        <button className="btn btn-secondary" onClick={handleCancel}>
          Cancel — Enter Manually
        </button>
      </div>
    </div>
  );
}

function stopStream(stream) {
  try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
}

function friendlyError(e) {
  if (e.name === 'NotAllowedError') return 'Camera access denied. Please allow camera access and try again.';
  if (e.name === 'NotFoundError')   return 'No camera found on this device.';
  if (e.name === 'OverconstrainedError') return 'Camera constraint failed — try reloading the page.';
  return 'Could not start camera: ' + e.message;
}
